import { Component, ChangeDetectorRef, input, effect, output, ChangeDetectionStrategy } from "@angular/core";
import { SelectionModel } from "@angular/cdk/collections";
import { FormControl, FormGroup } from "@angular/forms";
import { take } from "rxjs";
import { AppService } from "../../../app.service";
import { AuctionType } from "../../../models/auctionType";
import { PropertyFeatures } from "../../../models/propertyFeatures";
import { PropertyFilterParameters } from "../../../models/propertyFilterParameters";
import { PropertySubType } from "../../../models/propertySubType";
import { PropertyType } from "../../../models/propertyType";
import { SaleType } from "../../../models/saleType";
import { State } from "../../../models/state";
import { GeneralDataService } from "../../../services/GeneralDataService";
import { PropertyService } from "../../../services/PropertyService";

@Component({
  standalone: false,
  selector: 'app-all-filters',
  templateUrl: './all-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./all-filters.component.scss']
})
export class AllFiltersComponent {
  readonly onPropertyFilterChange = output<PropertyFilterParameters>();
  readonly onClose = output<any>();

  readonly propertyFilterParameters = input<PropertyFilterParameters|undefined>(undefined);
  readonly routerPath = input<string|undefined>(undefined);

  private currentPropertyFilterParameters?: PropertyFilterParameters;

  // Auction types
  public auctionTypes: AuctionType[] = [];
  public auctionTypeSelection = new SelectionModel<number>(true, []);
  private committedAuctionTypeIds: number[] = [];

  // Property types / subtypes
  public propertyTypes: PropertyType[] = [];
  public subTypeSelection = new SelectionModel<number>(true, []);
  private committedSubTypeIds: number[] = [];

  // States
  public states: State[] = [];
  public stateSelection = new SelectionModel<number>(true, []);
  private committedStateIds: number[] = [];

  // Sale types
  public saleTypes: SaleType[] = [];
  public saleTypeSelection = new SelectionModel<number>(true, []);
  private committedSaleTypeIds: number[] = [];

  // Property features
  public selectedPropertyFeatures: PropertyFeatures | undefined;
  public propertyFeaturesForm: FormGroup;

  public bedRoomFilterValues: { label: string, value: number|undefined }[] = [{ label: "Any", value: undefined }, { label: "1+ Beds", value: 1 }, { label: "2+ Beds", value: 2 }, { label: "3+ Beds", value: 3 }, { label: "4+ Beds", value: 4 }];
  public bathRoomFilterValues: { label: string, value: number|undefined }[] = [{ label: "Any", value: undefined }, { label: "1+ Baths", value: 1 }, { label: "2+ Baths", value: 2 }, { label: "3+ Baths", value: 3 }, { label: "4+ Baths", value: 4 }];
  public garageFilterValues: { label: string, value: number|undefined }[] = [{ label: "Any", value: undefined }, { label: "1+ Cars", value: 1 }, { label: "2+ Cars", value: 2 }, { label: "3+ Cars", value: 3 }, { label: "4+ Cars", value: 4 }];

  constructor(private propertyService: PropertyService, private gdService: GeneralDataService, private appService: AppService, private cdRef: ChangeDetectorRef) {
    this.propertyFeaturesForm = new FormGroup({
      beds: new FormControl<{ label: string, value: number|undefined }>(this.bedRoomFilterValues[0]),
      baths: new FormControl<{ label: string, value: number|undefined }>(this.bathRoomFilterValues[0]),
      garages: new FormControl<{ label: string, value: number|undefined }>(this.garageFilterValues[0]),
      areaFrom: new FormControl<number|undefined>(undefined),
      areaTo: new FormControl<number|undefined>(undefined)
    });

    effect(() => {
      this.currentPropertyFilterParameters = this.propertyFilterParameters();
    });

    // currently we do not show make offer / buy now
    this.auctionTypes = this.appService.auctionTypes.filter(at => at.id != 2);

    this.propertyService.getPropertyTypes().pipe(take(1)).subscribe(propertyTypes => {
      this.propertyTypes = propertyTypes;
    });
    this.gdService.getStates().pipe(take(1)).subscribe(states => {
      this.states = states;
    });
    this.propertyService.getSaleTypes().pipe(take(1)).subscribe(saleTypes => {
      this.saleTypes = saleTypes;
    });
  }

  ngAfterViewInit() {
    this.setPropertyFilterParameters();
  }

  get showAuctionTypeFilter() {
    return this.routerPath() == "/available";
  }

  get allAuctionTypesSelected(): boolean {
    return this.committedAuctionTypeIds.length === 0;
  }

  get allPropertyTypesSelected(): boolean {
    return this.committedSubTypeIds.length === 0;
  }

  get allStatesSelected(): boolean {
    return this.committedStateIds.length === 0;
  }

  get allSaleTypesSelected(): boolean {
    return this.committedSaleTypeIds.length === 0;
  }

  get propertyFeatureSelected(): boolean {
    return this.selectedPropertyFeatures?.definedFeatures? true : false;
  }

  get menuLabel(): string {
    let label = "Property Features";
    if (this.propertyFeatureSelected) label += ` (${this.selectedPropertyFeatures?.definedFeatures})`;
    return label;
  }

  // --- checked / indeterminate helpers (driven by staged selection) ---

  public isAuctionTypeChecked(at: AuctionType): boolean {
    return this.auctionTypeSelection.isSelected(at.id);
  }

  public isPropertyTypeChecked(pt: PropertyType): boolean {
    return pt.subTypeList.length > 0 && pt.subTypeList.every(pst => this.subTypeSelection.isSelected(pst.id));
  }

  public isPropertyTypeIndeterminate(pt: PropertyType): boolean {
    const selectedCount = pt.subTypeList.filter(pst => this.subTypeSelection.isSelected(pst.id)).length;
    return selectedCount > 0 && selectedCount < pt.subTypeList.length;
  }

  public isSubTypeChecked(pst: PropertySubType): boolean {
    return this.subTypeSelection.isSelected(pst.id);
  }

  public isStateChecked(s: State): boolean {
    return this.stateSelection.isSelected(s.id);
  }

  public isSaleTypeChecked(st: SaleType): boolean {
    return this.saleTypeSelection.isSelected(st.id);
  }

  // --- toggles (staged only, no emit) ---

  public toggleAllAuctionTypes(checked: boolean) {
    if (checked) this.auctionTypeSelection.clear();
  }

  public toggleAuctionType(at: AuctionType) {
    this.auctionTypeSelection.toggle(at.id);
  }

  public toggleAllPropertyTypes(checked: boolean) {
    if (checked) this.subTypeSelection.clear();
  }

  public togglePropertyType(pt: PropertyType) {
    const ids = pt.subTypeList.map(pst => pst.id);
    if (this.isPropertyTypeChecked(pt)) this.subTypeSelection.deselect(...ids);
    else this.subTypeSelection.select(...ids);
  }

  public toggleSubType(pst: PropertySubType) {
    this.subTypeSelection.toggle(pst.id);
  }

  public toggleAllStates(checked: boolean) {
    if (checked) this.stateSelection.clear();
  }

  public toggleState(s: State) {
    this.stateSelection.toggle(s.id);
  }

  public toggleAllSaleTypes(checked: boolean) {
    if (checked) this.saleTypeSelection.clear();
  }

  public toggleSaleType(st: SaleType) {
    this.saleTypeSelection.toggle(st.id);
  }

  // --- preset / reset / apply / clear ---

  public presetPropertyFilterParameters(parameters: PropertyFilterParameters) {
    this.currentPropertyFilterParameters = parameters;
    this.setPropertyFilterParameters();
  }

  private setPropertyFilterParameters() {
    if (this.currentPropertyFilterParameters) {
      const auctionIds = (this.currentPropertyFilterParameters.auctionTypes ?? []).map(at => at.id);
      this.committedAuctionTypeIds = [...auctionIds];
      this.auctionTypeSelection = new SelectionModel<number>(true, auctionIds);

      const subTypeIds = (this.currentPropertyFilterParameters.propertyTypes ?? []).flatMap(pt => pt.subTypeIdList);
      this.committedSubTypeIds = [...subTypeIds];
      this.subTypeSelection = new SelectionModel<number>(true, subTypeIds);

      const stateIds = (this.currentPropertyFilterParameters.states ?? []).map(s => s.id);
      this.committedStateIds = [...stateIds];
      this.stateSelection = new SelectionModel<number>(true, stateIds);

      const saleTypeIds = (this.currentPropertyFilterParameters.saleTypes ?? []).map(st => st.id);
      this.committedSaleTypeIds = [...saleTypeIds];
      this.saleTypeSelection = new SelectionModel<number>(true, saleTypeIds);

      this.selectedPropertyFeatures = this.currentPropertyFilterParameters.propertyFeatures;
      this.resetFormFromCommittedFeatures();

      this.cdRef.detectChanges();
    }
  }

  private resetFormFromCommittedFeatures() {
    this.propertyFeaturesForm.get('beds')!.setValue(this.selectedPropertyFeatures?.beds ? this.bedRoomFilterValues.find(v => v.value == this.selectedPropertyFeatures?.beds) : this.bedRoomFilterValues[0]);
    this.propertyFeaturesForm.get('baths')!.setValue(this.selectedPropertyFeatures?.baths ? this.bathRoomFilterValues.find(v => v.value == this.selectedPropertyFeatures?.baths) : this.bathRoomFilterValues[0]);
    this.propertyFeaturesForm.get('garages')!.setValue(this.selectedPropertyFeatures?.garages ? this.garageFilterValues.find(v => v.value == this.selectedPropertyFeatures?.garages) : this.garageFilterValues[0]);
    this.propertyFeaturesForm.get('areaFrom')!.setValue(this.selectedPropertyFeatures?.areaFrom);
    this.propertyFeaturesForm.get('areaTo')!.setValue(this.selectedPropertyFeatures?.areaTo);
  }

  /** Falsy reason = dismissed without Apply -> discard every staged change back to committed. */
  public sidenavClosed(reason: any) {
    if (!reason) {
      this.auctionTypeSelection = new SelectionModel<number>(true, this.committedAuctionTypeIds);
      this.subTypeSelection = new SelectionModel<number>(true, this.committedSubTypeIds);
      this.stateSelection = new SelectionModel<number>(true, this.committedStateIds);
      this.saleTypeSelection = new SelectionModel<number>(true, this.committedSaleTypeIds);
      this.resetFormFromCommittedFeatures();
    }
  }

  private buildSelectedPropertyTypes(): PropertyType[] {
    return this.propertyTypes
      .map(pt => {
        const selectedSubTypes = pt.subTypeList.filter(pst => this.subTypeSelection.isSelected(pst.id));
        if (!selectedSubTypes.length) return null;

        const result = new PropertyType();
        result.id = pt.id;
        result.name = pt.name;
        result.subTypeList = selectedSubTypes;
        result.subTypeIdList = selectedSubTypes.map(pst => pst.id);
        return result;
      })
      .filter((pt): pt is PropertyType => pt !== null);
  }

  public onApplyClick() {
    if (!this.currentPropertyFilterParameters) this.currentPropertyFilterParameters = new PropertyFilterParameters();

    this.committedAuctionTypeIds = [...this.auctionTypeSelection.selected];
    this.currentPropertyFilterParameters.auctionTypes = this.allAuctionTypesSelected
      ? undefined
      : this.auctionTypes.filter(at => this.auctionTypeSelection.isSelected(at.id));

    this.committedSubTypeIds = [...this.subTypeSelection.selected];
    this.currentPropertyFilterParameters.propertyTypes = this.allPropertyTypesSelected
      ? undefined
      : this.buildSelectedPropertyTypes();

    this.committedStateIds = [...this.stateSelection.selected];
    this.currentPropertyFilterParameters.states = this.allStatesSelected
      ? undefined
      : this.states.filter(s => this.stateSelection.isSelected(s.id));

    this.committedSaleTypeIds = [...this.saleTypeSelection.selected];
    this.currentPropertyFilterParameters.saleTypes = this.allSaleTypesSelected
      ? undefined
      : this.saleTypes.filter(st => this.saleTypeSelection.isSelected(st.id));

    const pf = new PropertyFeatures();
    pf.beds = this.propertyFeaturesForm.get('beds')!.value?.value;
    pf.baths = this.propertyFeaturesForm.get('baths')!.value?.value;
    pf.garages = this.propertyFeaturesForm.get('garages')!.value?.value;
    pf.areaFrom = this.propertyFeaturesForm.get('areaFrom')!.value;
    pf.areaTo = this.propertyFeaturesForm.get('areaTo')!.value;
    this.selectedPropertyFeatures = pf;
    this.currentPropertyFilterParameters.propertyFeatures = pf;

    this.onPropertyFilterChange.emit(this.currentPropertyFilterParameters);
    this.onClose.emit(undefined);
  }

  public onClearClick() {
    this.auctionTypeSelection.clear();
    this.committedAuctionTypeIds = [];

    this.subTypeSelection.clear();
    this.committedSubTypeIds = [];

    this.stateSelection.clear();
    this.committedStateIds = [];

    this.saleTypeSelection.clear();
    this.committedSaleTypeIds = [];

    this.selectedPropertyFeatures = undefined;
    this.resetFormFromCommittedFeatures();

    if (!this.currentPropertyFilterParameters) this.currentPropertyFilterParameters = new PropertyFilterParameters();
    this.currentPropertyFilterParameters.clear();

    this.onPropertyFilterChange.emit(this.currentPropertyFilterParameters);
    this.onClose.emit(undefined);
  }
}

 